// Jeff's Toolkit.
// Contains different implementations of functions already defined in CommonMethods.ts
// Sometimes you need your own toolkit since the stuff in Common just doesn't do what you want.
// And since different parameters isn't a thing in Angular, we just make our own toolkit.
// Would be nice if this could eventually be add in Common, but too much work for now.

// getFormControl function
// Basically does the same as the version from Common, but works nicer with nameof
import {AbstractControl, FormGroup} from "@angular/forms";

export function getFormControl<T>(form: FormGroup, nameFunction: ((obj: T) => any)): AbstractControl {
    const name = nameof<T>(nameFunction);
    return form.controls[name.toString()];
}

export function getFormValue<T>(form: FormGroup, nameFunction: ((obj: T) => any)): any {
    const formControl = getFormControl(form, nameFunction);
    return formControl.value;
}

export function setFormControlValue<T>(formGroup: FormGroup, nameFunction: ((obj: T) => any), value: any): void {
    const formControl = getFormControl(formGroup, nameFunction);
    formControl.setValue(value);
}
// End of getFormControl


// nameof function
// A version of nameof which actually works with refactors
// Copied from https://github.com/IRCraziestTaxi/ts-simple-nameof under MIT License
export function nameof<T extends Object>(nameFunction: ((obj: T) => any) | { new(...params: any[]): T }): keyof T {
    const str = getString(nameFunction);
    const key = str as keyof T;

    if(key)
        return key;

    throw new Error("ts-simple-nameof: Error creating keyof from field name " + str);
}

// The basis of getting the name
// It's easier to just use the key though, so this is no longer exported
function getString<T extends Object>(nameFunction: ((obj: T) => any) | { new(...params: any[]): T }): string
{
    const fnStr = nameFunction.toString();

    // ES6 prop selector:
    // "x => x.prop"
    if (fnStr.includes("=>")) {
        return cleanseAssertionOperators(
            fnStr.substring(
                fnStr.lastIndexOf(".") + 1
            )
        );
    }

    return untestedWays(fnStr);
}

// These were included in the original code, but I haven't tested any of these.
// Since keyof wasn't part of the original code, I expect all of these to fail one way or another, but I've included them for completion
function untestedWays<T extends Object>(fnStr: string): string
{
    console.warn("ts-simple-nameof: Executing untested ways of namefinding");

    // ES6 class name:
    // "class ClassName { ..."
    if (
        fnStr.startsWith("class ")
        // Theoretically could, for some ill-advised reason, be "class => class.prop".
        && !fnStr.startsWith("class =>")
    ) {
        return cleanseAssertionOperators(
            fnStr.substring(
                "class ".length,
                fnStr.indexOf(" {")
            )
        );
    }

    // ES5 prop selector:
    // "function (x) { return x.prop; }"
    // webpack production build excludes the spaces and optional trailing semicolon:
    //   "function(x){return x.prop}"
    // FYI - during local dev testing i observed carriage returns after the curly brackets as well
    // Note by maintainer: See https://github.com/IRCraziestTaxi/ts-simple-nameof/pull/13#issuecomment-567171802 for explanation of this regex.
    const matchRegex = /function\s*\(\w+\)\s*\{[\r\n\s]*return\s+\w+\.((\w+\.)*(\w+))/i;

    const es5Match = fnStr.match(matchRegex);

    if (es5Match) {
        return es5Match[1]; //It's supposed to be either 1 or 3 to get the last prop. Just figure out which one you need
    }

    // ES5 class name:
    // "function ClassName() { ..."
    if (fnStr.startsWith("function ")) {
        return cleanseAssertionOperators(
            fnStr.substring(
                "function ".length,
                fnStr.indexOf("(")
            )
        );
    }

    // Invalid function.
    throw new Error("ts-simple-nameof: Invalid function.");
}

function cleanseAssertionOperators(parsedName: string): string {
    return parsedName.replace(/[?!]/g, "");
}
// End of nameof
